import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Badge, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import { fetchInventorySkus, fetchInventoryStores } from '../api/inventory';
import { fetchOverview } from '../api/client';
import { t } from '../lib/i18n';
import type { InventorySkuRow, InventoryStoreRow, OverviewPayload, Store, User } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 });
const fmtPct = new Intl.NumberFormat('es-MX', { style: 'percent', minimumFractionDigits: 1 });

interface StoreRow {
  sid: number;
  name: string;
  inv_cost_mxn: number;
  margin90_mxn: number;
  gmroi: number | null;
  sku_count: number;
  a_count: number;
}

function aggregate(skus: InventorySkuRow[], stores: Store[]): StoreRow[] {
  const byId = new Map<number, StoreRow>();
  for (const s of stores) {
    byId.set(s.sid, { sid: s.sid, name: s.name, inv_cost_mxn: 0, margin90_mxn: 0, gmroi: null, sku_count: 0, a_count: 0 });
  }
  for (const r of skus) {
    let row = byId.get(r.sid);
    if (!row) {
      row = { sid: r.sid, name: `Tienda ${r.sid}`, inv_cost_mxn: 0, margin90_mxn: 0, gmroi: null, sku_count: 0, a_count: 0 };
      byId.set(r.sid, row);
    }
    row.inv_cost_mxn += r.inv_cost_mxn ?? 0;
    row.margin90_mxn += r.margin90_mxn ?? 0;
    row.sku_count += 1;
    if (r.abc_class === 'A') row.a_count += 1;
  }
  const out: StoreRow[] = [];
  for (const row of byId.values()) {
    row.gmroi = row.inv_cost_mxn > 0 ? row.margin90_mxn / row.inv_cost_mxn : null;
    if (row.sku_count > 0) out.push(row);
  }
  out.sort((a, b) => (b.gmroi ?? -Infinity) - (a.gmroi ?? -Infinity));
  return out;
}

function gmroiBadge(g: number | null, chain: number | null) {
  if (g == null || chain == null) return <span className="text-tremor-content">—</span>;
  const delta = g - chain;
  const color = delta >= 0.05 ? 'emerald' : delta <= -0.05 ? 'red' : 'gray';
  const sign = delta >= 0 ? '+' : '';
  return (
    <span className="inline-flex items-center gap-2">
      <span>{fmtPct.format(g)}</span>
      <Badge color={color}>{sign}{fmtPct.format(delta)}</Badge>
    </span>
  );
}

export default function InventoryGmroi() {
  const user = useOutletContext<User>();
  const [skus, setSkus] = useState<InventorySkuRow[] | null>(null);
  const [storeRows, setStoreRows] = useState<InventoryStoreRow[] | null>(null);
  const [overview, setOverview] = useState<OverviewPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    setErr(null);
    setSkus(null);
    Promise.all([fetchInventorySkus(), fetchInventoryStores(), fetchOverview(user.role)])
      .then(([s, st, o]) => { setSkus(s); setStoreRows(st); setOverview(o); })
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [user.role]);

  const rows = useMemo(() => {
    if (!skus) return [];
    return aggregate(skus, overview?.stores ?? []);
  }, [skus, overview]);

  // Chain-level GMROI from per-store roll-up (single source of truth).
  const chain = useMemo(() => {
    if (rows.length === 0) return { gmroi: null as number | null, invCost: 0, margin: 0 };
    let invCost = 0, margin = 0;
    for (const r of rows) { invCost += r.inv_cost_mxn; margin += r.margin90_mxn; }
    return { gmroi: invCost > 0 ? margin / invCost : null, invCost, margin };
  }, [rows]);

  const showCost = user.role !== 'staff';
  if (!showCost) {
    return <Text>{t.inventory.gmroiNoCost}</Text>;
  }

  return (
    <div className="space-y-4">
      <div>
        <Title>{t.inventory.gmroiTitle}</Title>
        <Text className="text-tremor-content text-sm">{t.inventory.gmroiSubtitle}</Text>
      </div>

      <Card className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label={t.inventory.kpi.chainGmroi} value={chain.gmroi == null ? '—' : fmtPct.format(chain.gmroi)} />
        <Kpi label={t.inventory.kpi.invCost}    value={fmtMxn.format(chain.invCost)} />
        <Kpi label="Margen 90d"                 value={fmtMxn.format(chain.margin)} />
        <Kpi label="Tiendas"                    value={String(rows.length)} />
      </Card>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && skus === null && <TableSkeleton rows={5} cols={6} />}
      {!err && rows.length === 0 && skus !== null && <EmptyState message="—" />}
      {!err && rows.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.inventory.gmroiCols.store}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.inventory.gmroiCols.invCost}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.inventory.gmroiCols.margin}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.inventory.gmroiCols.gmroi}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.inventory.gmroiCols.skuCount}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.inventory.gmroiCols.aCount}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.sid}>
                  <TableCell className="font-medium">{r.name} <span className="text-tremor-content text-xs">#{r.sid}</span></TableCell>
                  <TableCell className="text-right">{fmtMxn.format(r.inv_cost_mxn)}</TableCell>
                  <TableCell className="text-right">{fmtMxn.format(r.margin90_mxn)}</TableCell>
                  <TableCell className="text-right">{gmroiBadge(r.gmroi, chain.gmroi)}</TableCell>
                  <TableCell className="text-right">{r.sku_count}</TableCell>
                  <TableCell className="text-right">{r.a_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Reference: also surface the pre-rolled per-store JSON the backend
          ships when it's denser than what we just rolled up (e.g. tracks
          legacy stores with no current skus). */}
      {storeRows && storeRows.length > rows.length && (
        <Text className="text-xs text-tremor-content">
          Nota: {storeRows.length - rows.length} tienda(s) sin SKUs activos no se listan.
        </Text>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong text-lg font-semibold">{value}</div>
    </div>
  );
}
